import Image from '@/components/common/SpiritoryImage';
import { useRouter } from 'next/router';
import { ImagesBlogClockSvg } from '@/assets/images_blog.assets';
import DefaultLink from '../common/default/DefaultLink';
import LoadingComponent from '../common/LoadingComponent';
import LoadingImage from '../common/LoadingImage';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { cn } from '@/lib/utils';
import { SkuEnum } from '@/types/translations/SkuEnum';
import {
  TastingCardData,
  formatEventDateShort,
  formatEventTime,
} from '@/utilites/tasting-event';

/**
 * One tasting on the `/tastings` overview.
 *
 * A copy of `MagazinePageCard` rather than a generalisation of it: that
 * component is typed hard against `BlogPost` down to `reading_time` and
 * `contributor`, and the classes *are* the design — the two grids have to line
 * up pixel for pixel on the same page width. What differs is only what the
 * three slots say: the badge is the start time rather than a reading time, the
 * line above the headline is the venue rather than a category, and the row
 * underneath is the host and the size of the room rather than an author.
 *
 * The card links to `/tastings/{slug}`, the tasting's own page. There
 * is one detail page for a tasting and this is not a second one.
 */
export default function TastingCard({
  tasting,
  mainCard = false,
}: {
  tasting: TastingCardData | null;
  mainCard?: boolean;
}): JSX.Element {
  const { locale = 'en' } = useRouter();
  const { translate } = useTypedTranslation<SkuEnum>('sku');

  if (tasting === null) {
    return (
      <LoadingComponent
        as="div"
        loading={true}
        componentProps={{
          className: cn({
            'tw-w-full': mainCard,
          }),
        }}
      >
        <article>
          <div className="tw-relative">
            <LoadingImage
              className={cn('max-nine:tw-h-40 max-nine:tw-w-full', {
                ['tw-h-[495px] tw-w-[1325px]']: mainCard,
                ['tw-h-[196px] tw-w-[427px]']: !mainCard,
              })}
              loading={true}
            >
              <></>
            </LoadingImage>
          </div>
          <div className="tw-flex tw-w-full tw-flex-col tw-pt-6">
            <div className="tw-flex tw-flex-col tw-gap-4">
              <span className="tw-mb-0 tw-text-left tw-text-sm tw-font-normal tw-text-text">
                &nbsp;
              </span>
              <div className="tw-flex tw-flex-col tw-gap-1">
                <h3
                  className={cn(
                    'tw-line-clamp-2 tw-min-h-[2lh] tw-text-left tw-font-semibold tw-text-text',
                    {
                      ['tw-text-2xl']: mainCard,
                      ['tw-text-lg']: !mainCard,
                    }
                  )}
                >
                  &nbsp;
                </h3>
              </div>
            </div>
          </div>
        </article>
      </LoadingComponent>
    );
  }

  const href = `/tastings/${tasting.slug}`;
  const date = formatEventDateShort(tasting.startsAt, locale);
  const time = formatEventTime(tasting.startsAt, locale);
  const seats = tasting.capacity
    ? translate(SkuEnum['tasting.spots']).replace('{count}', String(tasting.capacity))
    : null;

  return (
    <div
      className={cn({
        'tw-w-full': mainCard,
      })}
    >
      <article className="max-sm:tw-rounded-xl max-sm:tw-border max-sm:!tw-border-solid max-sm:tw-border-gray-200 max-sm:tw-shadow">
        <DefaultLink className="tw-relative tw-w-full" href={href}>
          {tasting.imageUrl ? (
            <Image
              src={tasting.imageUrl}
              alt={tasting.title}
              width={mainCard ? 1325 : 427}
              height={mainCard ? 495 : 196}
              className="tw-w-full tw-rounded-xl tw-object-cover max-nine:tw-h-40 max-sm:tw-rounded-b-none"
              priority={mainCard}
              fetchPriority={mainCard ? 'high' : undefined}
            />
          ) : (
            // A ticket always has a product image, so this is the case where
            // the record points at nothing at all. A flat panel keeps the row
            // heights aligned; a broken `next/image` would not.
            <div
              className={cn(
                'tw-w-full tw-rounded-xl tw-bg-customGray max-nine:tw-h-40 max-sm:tw-rounded-b-none',
                {
                  ['tw-h-[495px]']: mainCard,
                  ['tw-h-[196px]']: !mainCard,
                }
              )}
            />
          )}
          {date ? (
            <span className="tw tw-absolute tw-bottom-5 tw-right-5 tw-z-10 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-md tw-bg-white tw-bg-opacity-70 tw-p-2 tw-text-sm tw-font-medium tw-text-[#15253FCC] max-nine:tw-text-xs">
              <Image src={ImagesBlogClockSvg} alt="" width={16} height={16} />
              {time ? `${date}, ${time}` : date}
            </span>
          ) : null}
        </DefaultLink>
        <div className="tw-flex tw-w-full tw-flex-col tw-pt-6 max-sm:tw-p-[15px]">
          <div className="tw-flex tw-flex-col tw-gap-4">
            <span className="tw-mb-0 tw-text-left tw-text-sm tw-font-normal tw-text-text">
              {tasting.venue}
            </span>
            <div className="tw-flex tw-flex-col tw-gap-1">
              <DefaultLink href={href}>
                <h3
                  className={cn(
                    'tw-line-clamp-2 tw-min-h-[2lh] tw-text-left tw-font-semibold tw-text-text tw-underline-offset-4 max-nine:tw-text-sm',
                    {
                      ['tw-text-2xl']: mainCard,
                      ['tw-text-lg']: !mainCard,
                    }
                  )}
                >
                  {tasting.title}
                </h3>
              </DefaultLink>
              <p className="tw-line-clamp-2 tw-text-left tw-text-[15px] tw-font-normal tw-text-black max-nine:tw-text-xs">
                {tasting.description}
              </p>
            </div>
          </div>

          <div className="tw-flex tw-w-full tw-items-center tw-justify-between tw-pb-2">
            <span className="tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-text-[#00020680]">
              {seats}
            </span>
            {tasting.hostedBy ? (
              <span className="tw-text-sm tw-font-semibold tw-text-text">
                {translate(SkuEnum['tasting.hosted_by'])} {tasting.hostedBy}
              </span>
            ) : null}
          </div>
        </div>
      </article>
    </div>
  );
}
